import React, {useState} from 'react';
import {fastapi} from 'api';
import {Alert, Buttons, Form, Inputs, Typography} from 'components';
import {useMarel, useUser} from 'hooks';
import {sendTag} from 'store/Tags';

import {makeStyles} from '@material-ui/styles';
import {encryptCredential} from '../../StepLogin';
import {getAllMarelAccounts} from '../../../../../../utils/accounts/marelAccounts';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '50%',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            padding: theme.spacing(2, 2, 1)
        },
        padding: theme.spacing(3, 4, 2),
        background: theme.palette.white
    }
}));

function getErrorMessage(error) {
    const status = error.response?.status;
    if (status === 401 || status === 403) {
        const parsed = JSON.parse(error.response?.data?.message);
        return parsed?.message ?? 'Accès non autorisé.';
    }
    return 'Une erreur est survenue, veuillez recommencer ultérieurement.';
}

const LoginForm = ({setCredentials, user, setSubStep, publicKey, setSessionId}) => {
    return (
        <Form
            schema={{
                identifier: {
                    presence: {
                        allowEmpty: false,
                        message: 'Veuillez renseigner votre numéro fiscal'
                    },
                    format: {
                        pattern: '^[0-9]{13}$',
                        message: 'Votre numéro fiscal est invalide'
                    }
                },
                password: {
                    presence: {
                        allowEmpty: false,
                        message: 'Veuillez renseigner votre mot de passe impôts.gouv'
                    },
                    length: {
                        minimum: 8,
                        message: 'Votre mot de passe doit comporter au moins 8 caractères'
                    }
                }
            }}
            submit={(formState, setFormState) =>
                fastapi
                    .post('/simulators/marel/login/dgfip', {
                        identifier: encryptCredential(formState.values.identifier, publicKey),
                        password: encryptCredential(formState.values.password, publicKey)
                    })
                    .then((response) => {
                        setCredentials({
                            identifier: formState.values.identifier,
                            password: formState.values.password
                        });
                        setSessionId(response.data.session_id);
                        setSubStep(1); // StepSimulation
                        sendTag(user, 'cnav::logindgfip');
                    })
                    .catch((error) => {
                        setFormState({
                            ...formState,
                            errors: {
                                password: [getErrorMessage(error)]
                            },
                            isValid: false
                        });
                    })
            }
            variant="transparent"
        >
            <Alert
                title={'Information'}
                message={
                    <>
                        <Typography>
                            Si vous rencontrez des difficultés de connexion avec impot.gouv, veuillez utiliser le
                            parcours Ameli ou utiliser vos identifiants InfoRetraite.
                        </Typography>
                        <Typography>
                            Attention : Vous avez 5 essais maximum avant blocage temporaire de votre compte.
                        </Typography>
                    </>
                }
                severity={'info'}
            />
            <Inputs.Text
                name="identifier"
                label="Numéro fiscal (13 chiffres)"
            />
            <Inputs.Text
                name="password"
                label="Mot de passe"
                type="password"
            />
            <Buttons.Default
                submit
                label="Se connecter"
                color="blueLight"
                next
                center
            />
        </Form>
    );
};

const TwoFactorForm = ({sessionId, user, setStepParent, setSubStep}) => {
    const {marel, setMarel} = useMarel();
    const {setUser} = useUser();
    const [resendResponse, setResendResponse] = useState(null);

    return (
        <>
            <Typography>Veuillez saisir le code de sécurité qui vous a été envoyé</Typography>
            <Alert
                title={'Information'}
                message={
                    <Typography>
                        Attention : Vous avez 3 essais maximum avant blocage temporaire de votre compte.
                    </Typography>
                }
                severity={'info'}
            />
            {resendResponse && (
                <Alert
                    message={<Typography>{resendResponse.message}</Typography>}
                    severity={resendResponse.status === 200 ? 'info' : 'error'}
                />
            )}
            <Form
                schema={{
                    code: {
                        presence: {
                            allowEmpty: false,
                            message: 'Veuillez renseigner votre code de sécurité'
                        },
                        format: {
                            pattern: '^[0-9]{6}$',
                            message: 'Votre code doit comporter 6 chiffres'
                        }
                    }
                }}
                submit={(formState, setFormState) =>
                    fastapi
                        .post('/simulators/marel/login/dgfip/two-factor', {
                            ...formState.values,
                            session_id: sessionId
                        })
                        .then((response) => {
                            setSubStep(0);
                            setStepParent(2); // StepSimulation
                            sendTag(user, 'cnav::logindgfiptwofactor');
                            if (sessionId && user.origin !== 'collective') {
                                fastapi
                                    .post('/simulators/marel/contracts', {session_id: response.data.session_id})
                                    .then((response) => {
                                        setMarel({...marel, ...response.data});
                                        setUser({
                                            ...user,
                                            accounts: getAllMarelAccounts(user.accounts, response.data?.contracts)
                                        });
                                        sendTag(user, 'cnav::contracts');
                                    })
                                    .catch((error) => {
                                        console.log(error);
                                    });
                            }
                        })
                        .catch((error) => {
                            setFormState({
                                ...formState,
                                errors: {
                                    code: [getErrorMessage(error)]
                                },
                                isValid: false
                            });
                        })
                }
                variant="transparent"
            >
                <Inputs.Text
                    name="code"
                    label="Code de sécurité"
                />
                <Typography>Vous n'avez pas reçu le code ? Vérifiez votre dossier Spam ou Indésirable</Typography>
                <Buttons.Chevron
                    onClick={() => {
                        fastapi
                            .post('/simulators/marel/login/dgfip/resend_code', {session_id: sessionId})
                            .then((response) => {
                                setResendResponse({
                                    message: 'Un code de sécurité vient de vous être renvoyé.',
                                    status: 200
                                });
                            })
                            .catch((error) => {
                                setResendResponse(JSON.parse(error.response?.data?.message));
                            });
                    }}
                    label="Renvoyer le code"
                    center
                />
                <Buttons.Default
                    submit
                    label="Valider"
                    color="blueLight"
                    next
                    center
                />
            </Form>
        </>
    );
};

function SubStepLoginDgfip({setStep, setStepParent, sessionId, setSessionId}) {
    const classes = useStyles();
    const {user} = useUser();
    const {setCredentials, publicKey} = useMarel();
    const [subStep, setSubStep] = useState(0);

    return (
        <div className={classes.root}>
            <Typography variant="h4">Connexion simplifiée avec FranceConnect</Typography>

            {subStep === 0 && (
                <LoginForm
                    user={user}
                    setSubStep={setSubStep}
                    setCredentials={setCredentials}
                    publicKey={publicKey}
                    setSessionId={setSessionId}
                    subStep={subStep}
                />
            )}
            {subStep === 1 && (
                <TwoFactorForm
                    user={user}
                    setStep={setStep}
                    sessionId={sessionId}
                    setSubStep={setSubStep}
                    setStepParent={setStepParent}
                />
            )}
            <Buttons.Chevron
                label="Retour"
                onClick={() => {
                    setSubStep(0);
                    setStep(1);
                }} // SubStepChannel
                center
            />
        </div>
    );
}

export default SubStepLoginDgfip;
